import { getEase, Tween } from "@westpierstudio/luma/createjs";
import { View } from "@westpierstudio/luma/mvc";
import { ElasticCache } from "@westpierstudio/luma/utils/elastic-cache";
import { GameServices } from "game/GameServices";
import { Reel } from "./Reel";
import { ReelsLayout, SpinConfig } from "./ReelsModel";
import { ReelStrips } from "servertypes/gameevents/OpenGame";
import { CoreSymbolNumberMap, ReelSetSymbols } from "servertypes/SharedTypes";
import { Spine } from "pixi-spine";
import { cloneDeep } from "lodash";
import { Signal } from "type-signals";

const POT_COIN_REELS = [2];

class ReelsSignals {
    bonusSymbolLand = new Signal<(reelIndex: number) => void>();
    reelStopSound = new Signal();
    reelStopPotCoin = new Signal<(reelIndex: number) => void>();
    spinComplete = new Signal();
    scatterWinComplete = new Signal();
    shakeComplete = new Signal();
    topSymbolInteract = new Signal();
}

export type SymbolCache = {[key: string]: ElasticCache<Spine>};

export class Reels extends View {
    private symbolCache: SymbolCache;
    private reels: Reel[] = [];
    private spinConfig!: SpinConfig;
    signals = new ReelsSignals();

    constructor(protected override services: GameServices) {
        super(services);

        this.symbolCache = this.setupSymbolCache();
    }

    setup(layout: ReelsLayout): void {

        this.services.factory.applySpec(this, {x: layout.x, y: layout.y});

        const {positions, reelCount, symbolSize} = layout;

        let reel;

        for (let i=0; i<reelCount; i++) {

            reel = new Reel(this.services, i, symbolSize.height, this.symbolCache);
            reel.x = positions[i].x;

            reel.signals.bonusSymbolLand.add((reelIndex: number) => this.signals.bonusSymbolLand.dispatch(reelIndex));
            reel.signals.reelStopSound.add(() => this.signals.reelStopSound.dispatch());
            reel.signals.reelStop.add(this.reelStopped, this);
            reel.signals.scatterWinComplete.add((reelIndex: number) => {
                if (reelIndex === 0) {
                    this.signals.scatterWinComplete.dispatch();
                }
            });
            reel.signals.topSymbolInteract.add(() => this.signals.topSymbolInteract.dispatch());

            this.addChild(reel);
            this.reels.push(reel);
        }
    }

    getSymbolGlobalPosition(reelIndex: number, symbolIndex: number) {
        return this.reels[reelIndex].getVisibleSymbolGlobalPosition(symbolIndex);
    }

    setCurrentReelSymbols(reels: ReelSetSymbols, inBonus: boolean, multipliers?: Partial<CoreSymbolNumberMap>): void {

        for (const [reelIndex, value] of reels.entries()) {
            this.reels[reelIndex].setSymbols(value, inBonus, multipliers);
        }
    }

    setReelStrips(reelStrips: ReelStrips): void {

        for (const [reelIndex, value] of reelStrips.entries()) {
            this.reels[reelIndex].setReelStrip(value);
        }
    }

    setSpinConfig(spinConfig: SpinConfig): void {
        this.spinConfig = spinConfig;

        for (const reel of this.reels) {
            reel.setSpinConfig(spinConfig);
        }
    }

    //generate a random stop position for each reel + swap the symbols on the map so they land on the result
    spin(spinResult: ReelSetSymbols, inBonus: boolean, playLandAnim: boolean[][],
        multipliers: Partial<CoreSymbolNumberMap>): void {

        const stops = [];
        let rnd_stop;

        for (const [reelIndex, reel] of this.reels.entries()) {

            rnd_stop = reel.getRandomReelStripIndex();
            stops.push(rnd_stop);

            /*
                provided result is reverse to reel strip order i.e:
                    stepping through a reel strip 7,3,6,4,1, with a stop index of 2 we'd expect
                    7,3,6 to be on the reels, with 6 at the top as it was the last symbol.
                    Instead we need to display 6,3,7, so reverse the provided result before subbing.
            */
            reel.swapReelStripSymbols(rnd_stop, cloneDeep(spinResult[reelIndex]).reverse());
        }

        this.spinToStops(stops, inBonus, playLandAnim, multipliers);
    }

    slamStop(spinResult: ReelSetSymbols, inBonus: boolean, playLandAnim: boolean[][],
        multipliers: Partial<CoreSymbolNumberMap>): void {

        for (const [i, reel] of this.reels.entries()) {
            reel.slampStop(spinResult[i], inBonus, playLandAnim[i], (i===0) ? multipliers : undefined);
        }
        this.signals.reelStopSound.dispatch();
    }

    shake(): void {

        const settings = this.services.resources.getSetting(`reelShake`);
        const {radius, shakeCount, totalTime, tween} = settings;

        const shakeTime = totalTime/shakeCount;
        const ease = getEase(tween);

        const baseX = this.x;
        const baseY = this.y;

        const moveTween = Tween.get(this);

        let x, y, distance, angle;

        for (let i=0; i<shakeCount-1; i++) {

            distance = radius * Math.sqrt(Math.random());
            angle = Math.random() * 2 * Math.PI;
            x = baseX + (distance * Math.cos(angle));
            y = baseY + (distance * Math.sin(angle));

            moveTween.to({x, y}, shakeTime, ease);
        }

        moveTween.to({x: baseX, y: baseY}, shakeTime, ease)
        .call(() => this.signals.shakeComplete.dispatch());
    }

    startWinAnims(animateState: boolean[][]): void {

        for (const [i, reel] of this.reels.entries()) {
            reel.startWinAnimations(animateState[i]);
        }
    }

    startScatterWinAnimationSequences(symbols: {column: number, row: number}[]) {

        for (const value of symbols) {
            this.reels[value.column].startScatterWinAnimationSequence(value.row);
        }
    }

    startScatterWinAnimationLoops(symbols: {column: number, row: number}[]) {

        for (const value of symbols) {
            this.reels[value.column].startScatterWinAnimationLoop(value.row);
        }
    }

    stopWinAnims(): void {

        for (const reel of this.reels) {
            reel.stopWinAnimations();
        }
    }

    setTopSymbolInteraction(interactive: boolean): void {

        for (const reel of this.reels) {
            reel.setTopSymbolInteraction(interactive);
        }
    }

    override reset(): void {

        for (const reel of this.reels) {
            reel.reset();
        }
    }

    private setupSymbolCache(): SymbolCache {

        const {factory, resources} = this.services;

        const symbolCache: SymbolCache = {};

        for (const [key, spec] of Object.entries(resources.getSpec(`symbol-spines`))) {
            symbolCache[key] = new ElasticCache<Spine>(
                () => {
                    return factory.createSpine(spec as any);
                },
                () => {

                },
                () => {

                }
            );
        }

        return symbolCache;
    }

    private spinToStops(reelStops: number[], inBonus: boolean, playLandAnim: boolean[][],
        multipliers: Partial<CoreSymbolNumberMap>): void {

        let spinTimePadding = 0;

        if (this.spinConfig.minSpinTimeActive) {

            //All reels start at the same time so last reel time equals the overall time
            const totalSpinTime = this.reels[this.reels.length-1].totalSpinTime;

            if (totalSpinTime < this.spinConfig.minSpinTime) {
                spinTimePadding = this.spinConfig.minSpinTime - totalSpinTime;
            }
        }

        for (const [i, reel] of this.reels.entries()) {
            reel.spin(reelStops[i], inBonus, playLandAnim[i], spinTimePadding, (i===0) ? multipliers : undefined);
        }
    }

    private reelStopped(id: number): void {

        if (POT_COIN_REELS.includes(id)) {
            this.signals.reelStopPotCoin.dispatch(id);
        }

        if (id === this.reels.length-1) {
            this.signals.spinComplete.dispatch();
        }
    }
}